import { ref } from 'vue';

import { createI18n } from 'vue-i18n';

import defaultConfig from '@/config/defaultConfig';
import { logAppError } from '@/config/sentryConfig';
import type enMessages from '@/locales/en.json';
import dayjs from '@/plugins/dayjs';
import {
  getTranslationRemoteTimeoutMs,
  isDevelopment
} from '@/utils/environment';
import { getFromCache, setToCache } from './cache';
import {
  SELECTED_LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage
} from './constants';
import { toDayjsLocale } from './dayjsLocaleMap';
import { ISO_COUNTRY_CODES } from './isoCountryCodes';
import {
  isSupportedLanguage,
  normalizeAndMigrateSelectedLanguage,
  toSupportedLanguage
} from './localeCompatibility';

// `import type` is erased at build time, so en.json is not bundled into the
// main chunk — locale JSON is loaded on demand via loadLanguageAsync.
export type MessageSchema = typeof enMessages;
type LocaleMessages = MessageSchema;

const isLocaleMessageObject = (value: unknown): value is LocaleMessages =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Max wait (ms) for the remote translation bundle before aborting and using
 * the bundled local file. Determined by the current environment stage:
 * - Production & Stage: 3000ms (3s)
 * - Dev & Feature: 5000ms (5s)
 */
const remoteTranslationTimeoutMs = getTranslationRemoteTimeoutMs();

const { fallbackLocale, selectedLanguage } = defaultConfig.i18n;
const supportedLanguages = SUPPORTED_LANGUAGES;

const normalizedSelectedLanguage = normalizeAndMigrateSelectedLanguage(
  selectedLanguage,
  supportedLanguages,
  SELECTED_LANGUAGE_STORAGE_KEY
);

// State.
// loadedLanguages tracks which locales already have messages applied. Plain Set
// (not a ref) because nothing in the UI observes it — it's an O(1) membership
// check used inside loadLanguageAsync only.
const loadedLanguages = new Set<SupportedLanguage>();
const currentLanguage = ref<SupportedLanguage | null>(
  normalizedSelectedLanguage
);

// Tracks the AbortController for the currently in-flight language load.
// When the user switches language before the previous request completes, the previous
// controller is aborted so the superseded request does not apply stale locale data.
let activeLangController: AbortController | null = null;

// i18n instance
export const i18n = createI18n<[MessageSchema], string>({
  legacy: false,
  // Explicit: enables $t / $te / $d in templates and Options API components (this.$t).
  // This is the default for legacy:false but stated clearly because the codebase mixes
  // Composition API (useI18n) and Options API (this.$t) components.
  globalInjection: true,
  // Guard against null localStorage on first visit — falls back to configured fallback locale.
  locale: currentLanguage.value ?? fallbackLocale,
  fallbackLocale,
  warnHtmlMessage: false,
  // Suppress missing-key and fallback-path warnings in production; they add noise without
  // actionable signal since both are handled gracefully by the loading strategy.
  // In development (dev/feature environments) they remain on so engineers catch missing keys early.
  missingWarn: isDevelopment,
  fallbackWarn: isDevelopment
  // No initial messages: loadLanguageAsync(detectLanguage()) runs at module init and
  // calls setLocaleMessage() once the locale resolves. Keeping messages out of createI18n
  // avoids bundling any locale JSON into the main chunk — all locale files are loaded on demand.
});

const loadLanguageAsync = async (lang: SupportedLanguage): Promise<void> => {
  // If language is already loaded, just switch to it — no network needed.
  if (loadedLanguages.has(lang)) {
    return setI18nLanguage(lang);
  }

  // Check if language is already cached in memory — no network needed.
  const cachedData = getFromCache<unknown>(lang);
  if (isLocaleMessageObject(cachedData)) {
    i18n.global.setLocaleMessage(lang, cachedData);
    loadedLanguages.add(lang);
    return setI18nLanguage(lang);
  }

  // Cancel any previous in-flight language load before starting a new one.
  // This prevents a slow earlier request (e.g. de) from overwriting a faster later one (e.g. fr)
  // when the user switches languages rapidly.
  if (activeLangController) {
    activeLangController.abort();
  }
  const controller = new AbortController();
  activeLangController = controller;

  // Load language messages from server or local files
  const messages = await loadMessages(lang, controller.signal);

  // Ignore result if this load was superseded by a later language switch.
  if (controller.signal.aborted) {
    return;
  }

  // Clear the active controller now that this load has completed.
  activeLangController = null;

  if (messages) {
    i18n.global.setLocaleMessage(lang, messages);
    loadedLanguages.add(lang);
    setI18nLanguage(lang);
  }
};

/**
 * Loads translation messages for the given locale using a two-stage strategy:
 *
 * 1. Remote fetch: requests the locale JSON bundle from the configured assets CDN
 *    (VITE_BM_ASSETS_HOST). The request is subject to a configurable timeout
 *    (remoteTranslationTimeoutMs). On success, the payload is validated as a non-null object
 *    before use and written to the localStorage cache (cache.ts) for subsequent loads.
 *
 * 2. Local fallback: if the remote request times out, is aborted, returns a non-2xx status,
 *    or returns a non-object payload (e.g. malformed/corrupt JSON), the function falls back
 *    to the statically bundled locale file at src/locales/{lang}.json.
 *
 * If both stages fail, returns null and the caller (loadLanguageAsync) leaves i18n state
 * untouched. If the bundled chunk is missing because a deploy removed it, the native
 * `vite:preloadError` handler in src/main.js reloads the app to the latest version; the
 * durable fix is keeping old hashed bundles available on deploy (see docs/deployment.md).
 *
 * @param {string} lang - The locale code to load (e.g. 'de', 'fr', 'en').
 * @param {AbortSignal} [cancellationSignal] - Optional signal from loadLanguageAsync that cancels
 *   this load when the user switches to a different language before this request completes.
 *   Distinct from the internal CDN timeout signal; cancellation is intentional and silent.
 * @returns {Promise<object|null>} Resolved translation message object, or null if all sources fail.
 */
const loadMessages = async (
  lang: SupportedLanguage,
  cancellationSignal?: AbortSignal
): Promise<LocaleMessages | null> => {
  // Try to load from remote server first
  try {
    const { default: apiClient } = await import('@/services/api');

    // Internal timeout controller — fires after remoteTranslationTimeoutMs to prevent indefinite hangs.
    const timeoutController = new AbortController();
    const timeoutId = window.setTimeout(
      () => timeoutController.abort(),
      remoteTranslationTimeoutMs
    );

    // Combine the timeout signal with the caller's cancellation signal so either one aborts the request.
    const signal = (() => {
      if (!cancellationSignal) {
        return timeoutController.signal;
      }

      const combined = new AbortController();
      const abort = () => combined.abort();

      // Attach listeners first, then check for already-aborted state. addEventListener('abort', …)
      // does not fire retroactively, so a signal that aborted between its creation and this point
      // would be missed without the explicit post-attach check.
      timeoutController.signal.addEventListener('abort', abort, { once: true });
      cancellationSignal.addEventListener('abort', abort, { once: true });

      if (timeoutController.signal.aborted || cancellationSignal.aborted) {
        combined.abort();
      }

      return combined.signal;
    })();

    let response;
    try {
      response = await apiClient.get(
        `${import.meta.env.VITE_BM_ASSETS_HOST}/translations/backoffice/locales/${lang}.json`,
        { signal }
      );
    } finally {
      window.clearTimeout(timeoutId);
    }

    if (isLocaleMessageObject(response?.data)) {
      setToCache(lang, response.data); // Cache for future use
      return response.data;
    }
    // Reachable but unusable payload (corrupt/empty) — fall through to the bundle.
  } catch {
    // Superseded by a later language switch is intentional; any other remote
    // failure (timeout / network / non-2xx) just falls through to the bundle.
    // The apiClient interceptor already records the failed request in Sentry.
    if (cancellationSignal?.aborted) {
      return null;
    }
  }

  // Fallback to the bundled locale file. If a deploy removed this hashed chunk,
  // the native vite:preloadError handler (src/main.js) reloads the app to the
  // latest version — the optional chaining just avoids throwing while that
  // reload is pending. A genuine, unrecoverable failure (reload guard already
  // fired) reaches the catch and is reported once.
  try {
    // If this chunk fails to load, Vite emits `vite:preloadError` and main.js may trigger a page reload.
    // Treat the module as maybe-undefined to avoid throwing while that reload is in progress.
    const response = (await import(`../locales/${lang}.json`)) as
      | { default: LocaleMessages }
      | undefined;
    return response?.default ?? null;
  } catch (error: unknown) {
    if (cancellationSignal?.aborted) {
      return null;
    }
    logAppError({
      error,
      functionName: 'loadMessages',
      section: 'i18n',
      level: 'fatal',
      message: `All translation sources failed for '${lang}'.`,
      metadata: { lang }
    });
    return null;
  }
};

const setI18nLanguage = (lang: SupportedLanguage): void => {
  // i18n.global.locale is a WritableComputedRef in Composition mode — assign via .value to preserve reactivity.
  (i18n.global.locale as unknown as { value: string }).value = lang;
  // BCP 47 uses hyphens, not underscores — `en-US` / `sr-Latn`, never `en_US` / `sr_Latn`.
  // Invalid tags can break browser language matching and assistive-tech locale selection.
  document.querySelector('html')?.setAttribute('lang', lang.replace('_', '-'));
  currentLanguage.value = lang;
  // Keep dayjs in sync with the active locale. Centralised here so every code path
  // (bootstrap, cached fast-switch, user-initiated switch) stays consistent.
  // i18n codes (e.g. 'fr_CA', 'sr_Latn') must be mapped to dayjs IDs (e.g. 'fr-ca', 'sr')
  // because dayjs silently keeps the previous locale if the ID is unknown.
  const dayjsLocale = toDayjsLocale(lang);
  dayjs.locale(dayjsLocale);
  dayjs.updateLocale(dayjsLocale, { weekStart: 1 });
};

/**
 * Sets the application language, persisting the selection to localStorage and loading the translation messages.
 *
 * This setter is intentionally soft: unsupported codes are logged via `logAppError`
 * and the function returns without throwing. Callers awaiting this should sync their
 * UI state from `currentLanguage`, not from the input argument.
 *
 * @param {string} lang - The locale code to switch to (e.g., 'de', 'fr', 'en'). Must be in supportedLanguages
 *   or this function returns early without updating state.
 * @returns {Promise<void>} Resolves when language loading completes.
 */
const setLanguage = async (lang: string): Promise<void> => {
  const resolvedLang = toSupportedLanguage(lang, supportedLanguages) ?? lang;

  // Validate language is supported before persisting to localStorage.
  // This prevents unsupported locale codes from being stored and causing fallback behavior on restart.
  if (!isSupportedLanguage(resolvedLang, supportedLanguages)) {
    logAppError({
      error: new Error('Unsupported language'),
      functionName: 'setLanguage',
      section: 'i18n',
      level: 'warning',
      message: `Language '${lang}' is not in supportedLanguages. Language switch ignored.`,
      metadata: { lang, resolvedLang, supportedLanguages }
    });
    return;
  }

  localStorage.setItem(SELECTED_LANGUAGE_STORAGE_KEY, resolvedLang);
  await loadLanguageAsync(resolvedLang);
};

// Memoized country map per locale. Rebuilding Intl.DisplayNames + mapping 250
// ISO codes is expensive; callers such as BMCountrySelector call getCountries
// inside a computed() that re-runs on every locale switch.
//
// Cache key is the post-normalisation locale so `getCountries('en_US')` and
// `getCountries('en-US')` share an entry (both resolve to `en-US`).
const countriesCache = new Map<string, Record<string, string>>();

const getCountries = (locale?: string): Record<string, string> => {
  let normalizedLocale = 'en';

  if (locale) {
    normalizedLocale = locale.replace('_', '-');
    try {
      new Intl.DisplayNames([normalizedLocale], { type: 'region' });
    } catch {
      const langOnly = normalizedLocale.split('-')[0];
      if (langOnly) {
        try {
          new Intl.DisplayNames([langOnly], { type: 'region' });
          normalizedLocale = langOnly;
        } catch {
          normalizedLocale = 'en';
        }
      } else {
        normalizedLocale = 'en';
      }
    }
  }

  const cached = countriesCache.get(normalizedLocale);
  if (cached) return cached;

  const regionNames = new Intl.DisplayNames([normalizedLocale], {
    type: 'region'
  });

  const result = Object.fromEntries(
    ISO_COUNTRY_CODES.map((code) => {
      try {
        const name = regionNames.of(code);
        return [code, name ?? code];
      } catch {
        return [code, code];
      }
    })
  ) as Record<string, string>;

  countriesCache.set(normalizedLocale, result);
  return result;
};

const detectLanguage = (): SupportedLanguage => {
  const browserLanguage = navigator.language.replace('-', '_');
  const formattedBrowserLang = browserLanguage.substring(0, 2);

  if (normalizedSelectedLanguage) return normalizedSelectedLanguage;

  const browserMatch = toSupportedLanguage(browserLanguage, supportedLanguages);
  if (browserMatch) return browserMatch;

  const browserShortMatch = toSupportedLanguage(
    formattedBrowserLang,
    supportedLanguages
  );
  if (browserShortMatch) return browserShortMatch;

  for (const lang of supportedLanguages) {
    if (lang.substring(0, 2) === formattedBrowserLang) return lang;
  }

  return fallbackLocale;
};

// Bootstrap: detect and load the initial locale once at module initialisation.
// Exported so callers that need to know when the first locale is fully loaded
// (messages applied, html[lang] set) can await this promise.
export const i18nBootstrapReady = loadLanguageAsync(detectLanguage());

export const useInternationalization = () => {
  return {
    i18n,
    currentLanguage,
    setLanguage,
    getCountries
  };
};
